import React, { useState } from 'react';
import { Modal, Typography, Spin, Input, Tag } from 'antd';
import { Button } from "../../misc/button/Button";
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BACKEND_URL } from '../../../config/config';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

// Labels pour les types de transition
const TRANSITION_TYPE_LABELS = {
  creation: 'Création',
  to_zero: 'Modification',
  zero_to_zero: 'Modification',
  from_zero: 'Modification',
};

const ModificationReviewModal = ({ visible, onCancel, onApprove, onReject, record, reason }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Détecter le type de demande
  const isAttestationRequest = typeof reason === 'object' && reason?.type === 'attestation';
  const attestationRequestId = record?.attestationRequestId;

  const handleDownloadDsn = () => {
    if (!isAttestationRequest || !attestationRequestId) return;

    axios
      .get(`${BACKEND_URL}/api/attestation-request/${attestationRequestId}/download-dsn`, {
        withCredentials: true,
        responseType: 'blob',
      })
      .then((response) => {
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'dsn.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement', error);
      });
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onApprove(record.id, isAttestationRequest, attestationRequestId);
      onCancel(); // Ferme le modal après succès
    } catch (error) {
      console.error("Erreur lors de l'approbation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (isAttestationRequest && !rejectionReason.trim()) {
      setShowRejectInput(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await onReject(record.id, isAttestationRequest, attestationRequestId, rejectionReason);
      onCancel(); // Ferme le modal après succès
      setShowRejectInput(false);
      setRejectionReason('');
    } catch (error) {
      console.error("Erreur lors du rejet:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={isAttestationRequest ? "Demande d'attestation" : "Demande de modification de déclaration"}
      open={visible}
      onCancel={() => {
        onCancel();
        setShowRejectInput(false);
        setRejectionReason('');
      }}
      destroyOnClose={true}
      footer={null}
      width={isAttestationRequest ? 700 : 600}
    >
      <Spin spinning={isSubmitting} tip="Traitement en cours...">
        <div style={{ marginBottom: '20px' }}>
          <Title level={5}>Détails de la demande</Title>

          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <Text strong style={{ width: '150px' }}>Code adhérent :</Text>
            <Text>{record?.membershipCode}</Text>
          </div>

          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <Text strong style={{ width: '150px' }}>Entreprise :</Text>
            <Text>{record?.companyName}</Text>
          </div>

          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <Text strong style={{ width: '150px' }}>Montant :</Text>
            <Text>{record?.feeAmount}</Text>
          </div>

          <div style={{ display: 'flex', marginBottom: '20px' }}>
            <Text strong style={{ width: '150px' }}>Date de déclaration :</Text>
            <Text>{record?.formatedInputedDate}</Text>
          </div>

          {isAttestationRequest ? (
            <>
              <Title level={5}>Type</Title>
              <div style={{ marginBottom: '20px' }}>
                <Tag color="blue">{TRANSITION_TYPE_LABELS[reason?.transitionType] || reason?.transitionType}</Tag>
              </div>

              {reason?.dsnFile && (
                <>
                  <Title level={5}>Fichier DSN</Title>
                  <div style={{
                    padding: '10px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <div>{reason.dsnFile.originalName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      Taille: {(reason.dsnFile.fileSize / 1024).toFixed(2)} KB
                    </div>
                    <Button
                      onClick={handleDownloadDsn}
                      children={
                        <span>
                          <DownloadOutlined /> Télécharger
                        </span>
                      }
                      style={{ marginTop: '10px' }}
                    />
                  </div>
                </>
              )}

              {showRejectInput && (
                <>
                  <Title level={5}>Raison du rejet (obligatoire)</Title>
                  <TextArea
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Veuillez indiquer la raison du rejet..."
                    style={{ marginBottom: '20px' }}
                  />
                </>
              )}
            </>
          ) : (
            <>
              <Title level={5}>Motif de la demande</Title>
              <Paragraph
                style={{
                  padding: '10px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  minHeight: '80px'
                }}
              >
                {reason || "Aucun motif fourni"}
              </Paragraph>
            </>
          )}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '20px'
        }}>
          <Button
            onClick={() => {
              onCancel();
              setShowRejectInput(false);
              setRejectionReason('');
            }}
            children="Annuler"
            style={{marginRight:20}}
          />
          <Button
            onClick={handleReject}
            children="Rejeter"
            isDisabled={isSubmitting}
            style={{marginRight:20}}
          />
          <Button
            onClick={handleApprove}
            children="Approuver"
            isDisabled={isSubmitting}
            style={{marginRight:20}}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default ModificationReviewModal;